/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useEffect, useMemo, useRef, useState, MutableRef, Ref } from 'preact/hooks';
import {
  getElementContainsFunc,
  handleEnterActionableMode,
  handleWrapActionableFocus
} from '../../utils/PRIVATE_collectionUtils';
import {
  FocusableElement,
  allTabbableElements,
  getActiveElement,
  getBodyElement,
  isTabbableElement
} from '../../utils/PRIVATE_tabbableUtils';
import { SizingInfo, TableColumn } from '../Table';
import {
  getCellType,
  getElementFromCell,
  getFirstFocusableCell,
  logicalCellExtractor
} from '../utils/TableDomUtils';
import { cellComparator } from '../utils/TableNavigationUtils';
import { applyScrollDetail } from '../utils/TableScrollUtils';
import { Cell, CurrentCellDetail } from '../../UNSAFE_TableView';

type UseFocusHandlingProps<K, D, C extends string> = {
  outerTableRef: Ref<HTMLDivElement>;
  tableId: string;
  isRtl: boolean;
  columnsArray: TableColumn<K, D, C>[];
  sizingInfoRef: MutableRef<SizingInfo>;
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>;
  cellSupportsTabbable: (cell: Cell<K, C>) => boolean;
  fullColumnWidths?: number[];
  currentCellOverride?: Cell<K, C>;
  onPersistCurrentCell?: (detail: CurrentCellDetail<K, C>) => void;
};

// constant timeout values for delays following a input gesture until enabling focus rings
const MOUSE_FOCUS_SHIFT_TIMEOUT = 0;
const TOUCH_FOCUS_SHIFT_TIMEOUT = 200;

// constant event.key values that trigger focus ring and 'scroll to current' logic
const NAVIGATION_KEYS = [
  ' ',
  'F2',
  'Enter',
  'Esc', // Firefox 36 and earlier uses 'Esc' instead of 'Escape'
  'Escape',
  'ArrowLeft',
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown'
];

/**
 * Hook that manages focus interactions on the Preact Table
 */
export function useFocusHandling<K extends string | number, D, C extends string>({
  outerTableRef,
  tableId,
  isRtl,
  columnsArray,
  sizingInfoRef,
  selectionExtensionElementRef,
  cellSupportsTabbable,
  fullColumnWidths,
  currentCellOverride,
  onPersistCurrentCell
}: UseFocusHandlingProps<K, D, C>) {
  // tracks whether the most recent 'blur' was caused by focus being lost by the browser window (or iframe)
  const blurFromWindowRef = useRef<boolean>(false);

  // tracks focus loss during render cycle for potential focus recapture cases
  const isRenderCycle = useRef<boolean>(true);
  isRenderCycle.current = true;
  const blurredRenderCycleElementRef = useRef<FocusableElement | null>(null);

  // track current 'focus info' state
  const hasFocusRef = useRef<boolean>(false);
  const [isTabbableMode, setIsTabbableMode] = useState<boolean>(false);
  const [isShowFocusRing, setIsShowFocusRing] = useState<boolean>(false);

  // track pointer down information
  const recentPointerRef = useRef<boolean>(false);
  const pointerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isShowFocusRingOverrideRef = useRef<boolean | null>(null);

  // tracks the current cell information
  const [currentCell, setCurrentCell] = useState<Cell<K, C> | undefined>(currentCellOverride);
  const currentCellRef = useRef<Cell<K, C> | undefined>(currentCellOverride);

  // tracks whether the table is handling a tab gesture that should allow focus to leave the table
  const [isTabbing, setIsTabbing] = useState<boolean>(false);

  // intercept onPersistCurrentCell calls to ensure internal state is updated appropriately
  const onCurrentCellChanged = useMemo(() => {
    return (detail: CurrentCellDetail<K, C>) => {
      setCurrentCell(detail.value);
      currentCellRef.current = detail.value;
      selectionExtensionElementRef.current = undefined;
      if (onPersistCurrentCell != null) {
        onPersistCurrentCell(detail);
      }
    };
  }, [onPersistCurrentCell, selectionExtensionElementRef]);

  // ensure any new currentCellOverride settings are applied only on that render
  const currentCellOverrideRef = useRef<Cell<K, C> | undefined>(currentCellOverride);
  if (currentCellOverrideRef.current !== currentCellOverride) {
    currentCellOverrideRef.current = currentCellOverride;
    if (!cellComparator(currentCell, currentCellOverride)) {
      onCurrentCellChanged({ value: currentCellOverride });
    }
  }

  // Helper function to set the Table's focus tracking state to reflect that it is inactive.
  const _setAsInactive = useCallback(() => {
    hasFocusRef.current = false;
    setIsTabbableMode(false);
    setIsShowFocusRing(false);
  }, []);

  // Helper function to set the Table's focus tracking state to reflect that on of its cell has focus.
  const _enableNavigationMode = useCallback(
    (cell: Cell<K, C>, skipScrollCellIntoView?: boolean) => {
      const rootElement = outerTableRef.current;
      if (rootElement != null) {
        setIsTabbableMode(false);
        setIsShowFocusRing(
          isShowFocusRingOverrideRef.current !== null
            ? isShowFocusRingOverrideRef.current
            : !recentPointerRef.current
        );
        if (!recentPointerRef.current && !skipScrollCellIntoView) {
          applyScrollDetail(
            rootElement,
            tableId,
            columnsArray,
            sizingInfoRef.current,
            { cell: cell, locationX: 'inView', locationY: 'inView' },
            isRtl,
            fullColumnWidths
          );
        }
        if (!cellComparator(cell, currentCell)) {
          onCurrentCellChanged({ value: cell });
        }
      }
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      sizingInfoRef,
      isRtl,
      currentCell,
      onCurrentCellChanged,
      fullColumnWidths
    ]
  );

  // Helper function to set the Table's focus tracking state to reflect that it is tabbable.
  const _enableTabbableMode = useCallback(
    (cell: Cell<K, C>, isKeyboard?: boolean, skipScrollCellIntoView?: boolean) => {
      const rootElement = outerTableRef.current;
      if (rootElement != null) {
        setIsTabbableMode(true);
        if (!isKeyboard) {
          setIsShowFocusRing(false);
          if (!cellComparator(cell, currentCell)) {
            onCurrentCellChanged({ value: cell });
          }
          if (!recentPointerRef.current && !skipScrollCellIntoView) {
            applyScrollDetail(
              rootElement,
              tableId,
              columnsArray,
              sizingInfoRef.current,
              { cell: cell, locationX: 'inView', locationY: 'inView' },
              isRtl,
              fullColumnWidths
            );
          }
        }
      }
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      sizingInfoRef,
      isRtl,
      currentCell,
      onCurrentCellChanged,
      fullColumnWidths
    ]
  );

  // Helper function to set the Table's focus tracking state to reflect that a user has
  // exited tabbable mode via the keyboard (esc or 'F2')
  const _disableTabbableMode = useCallback(
    (cell: Cell<K, C>) => {
      const rootElement = outerTableRef.current;
      if (rootElement != null) {
        setIsTabbableMode(false);
        const currentCellElement = getElementFromCell(rootElement, tableId, cell);
        if (currentCellElement != null) {
          currentCellElement.focus({ preventScroll: true });
        } else {
          rootElement.focus({ preventScroll: true });
        }
      }
    },
    [outerTableRef, tableId]
  );

  // PointerDown handler for the outer Table. Focus transfers that occur following a pointerdown
  // on desktop devices should not result in a focus ring being shown on the focused element
  const _pointerDownHandler = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType !== 'touch') {
        if (pointerTimerRef.current != null) {
          clearTimeout(pointerTimerRef.current);
        }
        recentPointerRef.current = true;
        pointerTimerRef.current = setTimeout(() => {
          recentPointerRef.current = false;
          if (isShowFocusRing) {
            setIsShowFocusRing(false);
          }
        }, MOUSE_FOCUS_SHIFT_TIMEOUT);
      }
    },
    [isShowFocusRing]
  );

  // TouchEnd handler for the outer Table. Focus transfers that occur following a touchend
  // on touch devices should not result in a focus ring being shown on the focused element
  const _touchEndHandler = useCallback(() => {
    if (pointerTimerRef.current != null) {
      clearTimeout(pointerTimerRef.current);
    }
    recentPointerRef.current = true;
    pointerTimerRef.current = setTimeout(() => {
      recentPointerRef.current = false;
      if (isShowFocusRing) {
        setIsShowFocusRing(false);
      }
    }, TOUCH_FOCUS_SHIFT_TIMEOUT);
  }, [isShowFocusRing]);

  // Focus handler for the outer Table. On focus, a focusable area needs to be 'current'.
  // If a 'current' area already exists, nothing further is needed. Otherwise, the first
  // focusable element should be made 'current'.
  const _onFocusHandler = useCallback(
    (event: FocusEvent) => {
      hasFocusRef.current = true;
      const rootElement = outerTableRef.current;
      if (rootElement != null) {
        // don't scroll current into view if previous blur was due to window focus loss
        const skipScrollCellIntoView = blurFromWindowRef.current;
        blurFromWindowRef.current = false;

        const targetElement = event.target as HTMLElement;
        if (targetElement === rootElement) {
          // handle overall root element receiving focus
          const cell =
            currentCell != null
              ? currentCell
              : (getFirstFocusableCell(rootElement, tableId) as Cell<K, C>);
          if (cell != null) {
            _enableNavigationMode(cell, skipScrollCellIntoView);
          }
        } else {
          const cell = logicalCellExtractor(
            targetElement,
            tableId,
            `[data-oj-table-focusable='${tableId}']`
          ) as Cell<K, C>;
          if (cell != null) {
            if (getCellType(targetElement) != null) {
              // handle individual focusable regions receiving focus
              _enableNavigationMode(cell, skipScrollCellIntoView);
            } else {
              _enableTabbableMode(cell, false, skipScrollCellIntoView);
            }
          }
        }
      }
      if (pointerTimerRef.current != null) {
        clearTimeout(pointerTimerRef.current);
        pointerTimerRef.current = null;
      }
      recentPointerRef.current = false;
      isShowFocusRingOverrideRef.current = null;
    },
    [outerTableRef, tableId, currentCell, _enableTabbableMode, _enableNavigationMode]
  );

  // Blur handler for the outer Table. When a blur is caused by the browser window itself
  // losing focus (changing tabs or leaving an iframe for example), an eventual re-focus due
  // to the window re-gaining focus needs to be handled differently (no auto-scroll mostly).
  const _onBlurHandler = useCallback(
    (event: FocusEvent) => {
      const rootElement = outerTableRef.current;
      if (rootElement != null) {
        if (event.target === document.activeElement) {
          // save focus ring state so it's set once focus comes back
          isShowFocusRingOverrideRef.current = isShowFocusRing;
        }
        const relatedTarget = event.relatedTarget as Element;
        const isUnknownRelatedTarget = relatedTarget == null;
        blurFromWindowRef.current = isUnknownRelatedTarget;
        if (isUnknownRelatedTarget || !getElementContainsFunc(rootElement, true)(relatedTarget)) {
          _setAsInactive();
          if (isRenderCycle.current) {
            // store element that loses focus during a render cycle for recapturing focus logic
            blurredRenderCycleElementRef.current = event.target as FocusableElement;
          }
        }
        setIsTabbing(false);
      }
    },
    [outerTableRef, isShowFocusRing, _setAsInactive]
  );

  // KeyDown handler for the outer Table. This specific handler tracks 'focus' transfer keys
  // Esc, F2, Enter, and Tab. It also ensures the currentCell is scrolled into view on any key
  const _onKeyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const rootElement = outerTableRef.current;
      if (rootElement != null && currentCell != null) {
        if (cellSupportsTabbable(currentCell)) {
          if (event.key === 'F2') {
            if (isTabbableMode) {
              _disableTabbableMode(currentCell);
            } else {
              _enableTabbableMode(currentCell, true);
            }
          } else if (event.key === 'Esc' || event.key === 'Escape') {
            // Firefox 36 and earlier uses 'Esc' instead of 'Escape'
            _disableTabbableMode(currentCell);
          } else if (event.key === 'Enter' && !isTabbableMode) {
            _enableTabbableMode(currentCell, true);
          }
        }
        if (NAVIGATION_KEYS.indexOf(event.key) > -1 && !isTabbableMode) {
          setIsShowFocusRing(true);
          applyScrollDetail(
            rootElement,
            tableId,
            columnsArray,
            sizingInfoRef.current,
            { cell: currentCell, locationX: 'inView', locationY: 'inView' },
            isRtl,
            fullColumnWidths
          );
        }
      }
      if (event.key === 'Tab' && !isTabbableMode) {
        setIsTabbing(true);
      }
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      sizingInfoRef,
      isRtl,
      isTabbableMode,
      currentCell,
      cellSupportsTabbable,
      fullColumnWidths,
      _enableTabbableMode,
      _disableTabbableMode
    ]
  );

  // Callback for the outer Table's FocusTracker usage.
  const onStartFocusTracking = useCallback(
    (detail: {
      activeElement: FocusableElement;
      hasFocusWithin: boolean;
      tabbableElements: FocusableElement[];
    }) => {
      const rootElement = outerTableRef.current;
      if (rootElement != null && currentCell != null) {
        handleEnterActionableMode(
          detail.hasFocusWithin && getCellType(detail.activeElement) == null,
          allTabbableElements(
            getElementFromCell(rootElement, tableId, currentCell) as FocusableElement
          ),
          (value?: Cell<K, D>) => {
            setIsTabbableMode(value != undefined);
          }
        );
      }
    },
    [outerTableRef, currentCell, tableId]
  );

  // Callback for the outer Table's FocusTracker usage.
  const onFocusStartEdge = useCallback((detail: { tabbableElements: FocusableElement[] }) => {
    handleWrapActionableFocus(true, detail.tabbableElements, (value?: Cell<K, D>) => {
      setIsTabbableMode(value != undefined);
    });
  }, []);

  // Callback for the outer Table's FocusTracker usage.
  const onFocusEndEdge = useCallback((detail: { tabbableElements: FocusableElement[] }) => {
    handleWrapActionableFocus(false, detail.tabbableElements, (value?: Cell<K, D>) => {
      setIsTabbableMode(value != undefined);
    });
  }, []);

  // Handle ensuring focus is properly set at the completion of every render cycle
  // NO DEPENDENCY ARRAY HERE TO ENSURE THIS RUNS ON EVERY RENDER CYCLE
  useEffect(() => {
    const blurredRenderCycleElement = blurredRenderCycleElementRef.current;
    blurredRenderCycleElementRef.current = null;
    isRenderCycle.current = false;

    const rootElement = outerTableRef.current;
    if (rootElement != null) {
      const activeElement = getActiveElement(rootElement);
      // if we were tracking focus during the render cycle, ensure we recapture focus if needed
      if (
        blurredRenderCycleElement != null &&
        !isTabbing &&
        activeElement === getBodyElement(rootElement) &&
        (!getElementContainsFunc(rootElement, true)(blurredRenderCycleElement) ||
          !isTabbableElement(blurredRenderCycleElement, true))
      ) {
        rootElement.focus({ preventScroll: true });
        return;
      }
      if (
        hasFocusRef.current &&
        currentCell != null &&
        !isTabbing &&
        cellComparator(currentCell, currentCellRef.current)
      ) {
        // ensure real browser focus is on the correct cell element
        const currentCellElement = getElementFromCell(rootElement, tableId, currentCell);
        if (currentCellElement != null) {
          // currentCell is valid, so set focus on it if needed
          if (
            (!isTabbableMode && activeElement !== currentCellElement) ||
            (isTabbableMode && !getElementContainsFunc(currentCellElement, true)(activeElement))
          ) {
            currentCellElement.focus({ preventScroll: true });
          }
        } else {
          // currentCell is not valid, so choose a new currentCell instead
          const newCurrentCell = getFirstFocusableCell(rootElement, tableId) as Cell<K, C>;
          if (newCurrentCell != null) {
            onCurrentCellChanged({ value: newCurrentCell });
          }
        }
      }
    }
  });

  return {
    focusHandlingProps: {
      onPointerDown: _pointerDownHandler,
      onTouchEnd: _touchEndHandler,
      onFocus: _onFocusHandler,
      onBlur: _onBlurHandler,
      onKeyDown: _onKeyDownHandler,
      tabIndex: isTabbing ? -1 : 0
    },
    currentCell,
    onCurrentCellChanged,
    isShowFocusRing,
    isTabbableMode,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge
  };
}
